/**
 * The viewer's last visit to the Briefs page (/app/twin/brief), kept in this browser for the sidebar's new-brief mark
 * (routes/_layout.tsx, briefsMarkVisible). Every read and write is guarded: where storage is missing or refuses a write
 * (a private window, blocked site data), the visit reads as unknown and the mark simply does not show.
 */
import { useSyncExternalStore } from "react";

export const BRIEFS_SEEN_KEY = "divineiot:briefs-seen";

/** Fired on this window when the page records a visit, so the sidebar reads it again. */
const SEEN_EVENT = "divineiot:briefs-seen";

// A refused write leaves an old visit (or none) in storage; reading it would keep the mark on for good.
let unwritable = false;

/** The last visit as stored (an ISO instant); null before the first visit; undefined when storage cannot be used. */
export function readBriefsSeen(): string | null | undefined {
  if (unwritable) return undefined;
  try {
    return localStorage.getItem(BRIEFS_SEEN_KEY);
  } catch {
    return undefined;
  }
}

/** Records a visit to the page now. */
export function markBriefsSeen(now: Date = new Date()): void {
  try {
    localStorage.setItem(BRIEFS_SEEN_KEY, now.toISOString());
  } catch {
    unwritable = true;
  }
  try {
    window.dispatchEvent(new Event(SEEN_EVENT));
  } catch {
    // No window to tell (a test or a worker): nothing reads it there.
  }
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(SEEN_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(SEEN_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

/** The last visit, kept current when the page records one (in this tab or another). */
export function useBriefsSeen(): string | null | undefined {
  return useSyncExternalStore(subscribe, readBriefsSeen, () => undefined);
}
